import React from "react";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Link, Stack, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { Check, Clear, Close, FilterAlt, Save, } from "@mui/icons-material";
import * as xlsx from "xlsx";
import { checkUserDetailsFromCookie } from "../../../api_calls/userManagement";
import ListingDataTableForModal from "../TabularLists/ListingDataTableForModal";
import Pagination from "../LoadMore/Pagination";
import { updateStateData } from "../../../Store/actions";
import { ListingCellCenter, ListingCellLeft, ListingHeaderContent, ListingIconMetric, ListingMetricValue } from "../TabularLists/styles";
import { SpaceBetweenStack, StyledCheckbox, StyledDarkRedInformationTypography, StyledWhiteCheckbox } from "../StyledComponents/styled";
import { SortIcon } from "../../../api_calls/utils";
import CloseButton from "../Buttons/CloseButton";
import ActionButton, { LightGreyButton } from "../Buttons/ActionButton";
import DisabledButton from "../Buttons/DisabledButton";
import SingleSelect from "../Selectors/SingleSelect";
import MultiSelect from "../Selectors/MultiSelect";

const StoreDetailTable = ({
    open,
    onClose,
    storeSelectionReduxKey,
}) => {
    const storeDetails = useSelector((state) => state.userSelections);
    const dispatchAction = useDispatch();
    // Local filter state for each filter type
    const [storeNameFilter, setStoreNameFilter] = React.useState([]);
    const [addressFilter, setAddressFilter] = React.useState([]);
    const [ownerFilter, setOwnerFilter] = React.useState([]);
    // active stores list
    const storesList = typeof storeDetails.stores_list !== "undefined" ? storeDetails.stores_list : [];

    // store selections
    const [tempSelectedStoreIds, setTempSelectedStoreIds] = React.useState([]);
    const [tempSelectedStoreNames, setTempSelectedStoreNames] = React.useState([]);
    const [tempAllSelected, setTempAllSelected] = React.useState(false);
    // to ensure uniqueness of options in filters
    const uniqueOptions = (options) => {
        const seen = new Set();
        return options.filter((option) => {
            if (seen.has(option.label)) return false;
            seen.add(option.label);
            return true;
        });
    };
    // filter options for the filters
    const storeOptions = storesList.map((s) => ({ id: s.id, label: s.name }));
    const addressOptions = storesList.map((s) => ({ id: s.id, label: s.address }));
    const ownerOptions = storesList.map((s) => ({ id: s.company.id, label: s.company.name }));
    // keep only unique options in the filters
    ownerOptions.splice(0, ownerOptions.length, ...uniqueOptions(ownerOptions));

    // Determine if any filters are currently applied
    const hasFilters = storeNameFilter.length || addressFilter.length || ownerFilter.length;

    const checkUserDetails = checkUserDetailsFromCookie();
    let userDetailObj = {};
    if (checkUserDetails.success === true) {
        userDetailObj = checkUserDetails.user_detail;
    }

    // pagination values
    const paginationStartValue =
        typeof storeDetails.store_selection_pagination !== "undefined" ? storeDetails.store_selection_pagination.store_list_start : 0;
    const paginationEndValue =
        typeof storeDetails.store_selection_pagination !== "undefined" ? storeDetails.store_selection_pagination.store_list_end : 10;

    // sorting config
    const [sortConfig, setSortConfig] = React.useState({
        name: { dir: "asc", active: true },
        address: { dir: "asc", active: false },
        zipcode: { dir: "asc", active: false },
        city: { dir: "asc", active: false },
        region: { dir: "asc", active: false },
        owner: { dir: "asc", active: false },
        available: { dir: "asc", active: false }
    });

    const manageSelectAll = (selectAll) => {
        if (selectAll) {
            const selectableStores = storesList.filter((store) => store.available !== false);

            setTempSelectedStoreIds(selectableStores.map((store) => store.id));
            setTempSelectedStoreNames(
                selectableStores.map((store) => ({
                    label: store.name,
                    value: store.name,
                }))
            );
            setTempAllSelected(true);
        } else {
            setTempSelectedStoreIds([]);
            setTempSelectedStoreNames([]);
            setTempAllSelected(false);
        }
    }

    const handleStoreCheckboxChange = (store, checked) => {
        if (checked) {
            addStoreToTempSelection(store);
        } else {
            removeStoreFromTempSelection(store);
        }
    };

    const handleApply = () => {
        dispatchAction(
            updateStateData({
                [storeSelectionReduxKey]: {
                    ...storeDetails[storeSelectionReduxKey],
                    store_ids: tempSelectedStoreIds,
                    selected_store_names: tempSelectedStoreNames,
                    all_selected: tempAllSelected,
                },
            })
        );

        onClose();
    };

    const handleCancel = () => {
        const reduxSelection = storeDetails[storeSelectionReduxKey] || {};

        setTempSelectedStoreIds(reduxSelection.store_ids || []);
        setTempSelectedStoreNames(reduxSelection.selected_store_names || []);
        setTempAllSelected(reduxSelection.all_selected || false);

        onClose();
    };

    // to apply search filter on the stores list
    const applyFilters = () => {
        const filteredStores = storesList.filter((store) => {
            const matchesStoreName =
                storeNameFilter.length === 0 ||
                storeNameFilter.some((filter) => filter.id === store.id);

            const matchesAddress =
                addressFilter.length === 0 ||
                addressFilter.some((filter) => filter.id === store.id);

            const matchesOwner =
                ownerFilter.length === 0 ||
                ownerFilter.some((filter) => filter.id === store.company.id);

            return matchesStoreName && matchesAddress && matchesOwner;
        });

        dispatchAction(
            updateStateData({
                store_list_filter_applied: true,
                detailed_stores_filtered: filteredStores
            })
        );

        return filteredStores;
    };

    // Clear filters and reset Redux state
    const clearFilters = () => {
        setStoreNameFilter([]);
        setAddressFilter([]);
        setOwnerFilter([]);
        dispatchAction(
            updateStateData({
                store_list_filter_applied: false,
                detailed_stores_filtered: []
            })
        );
    };

    // to return the sorted list items
    const sortedResults = (toBeSortedList) => {
        return toBeSortedList.slice().sort((a, b) => {
            const field = Object.keys(sortConfig).find(key => sortConfig[key].active === true);
            const sortDir = sortConfig[field]?.dir;

            if (field === "name") {
                return sortDir === "asc" ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
            } else if (field === "address") {
                return sortDir === "asc" ? a.address.localeCompare(b.address) : b.address.localeCompare(a.address);
            } else if (field === "zipcode") {
                return sortDir === "asc" ? String(a.zipCode).localeCompare(String(b.zipCode)) : String(b.zipCode).localeCompare(String(a.zipCode));
            } else if (field === "city") {
                return sortDir === "asc" ? a.city.localeCompare(b.city) : b.city.localeCompare(a.city);
            } else if (field === "region") {
                return sortDir === "asc" ? a.region?.name.localeCompare(b.region?.name) : b.region?.name.localeCompare(a.region?.name);
            } else if (field === "owner") {
                return sortDir === "asc" ? a.company?.name.localeCompare(b.company?.name) : b.company?.name.localeCompare(a.company?.name);
            }
            return 0;
        });
    };

    // to get the list of stores to be displayed based on whether search filter is applied or not
    let listItems = typeof storeDetails.store_list_filter_applied !== "undefined" && storeDetails.store_list_filter_applied === true ? storeDetails.detailed_stores_filtered : storesList;

    // to get the list of paginated and sorted stores to be displayed in the listing
    let storeListitems =
        listItems.length !== 0 ? sortedResults(listItems).slice(paginationStartValue, paginationEndValue) : [];

    const handleSortingParamChange = (sortField, sortDir) => {
        const sortOrder = sortDir === "asc" ? "desc" : "asc";
        setSortConfig((prev) => {
            const updated = { ...prev };
            Object.keys(updated).forEach(key => {
                updated[key] = { ...updated[key], active: key === sortField };
            });
            updated[sortField].dir = sortOrder;
            return updated;
        });
    };

    const addStoreToTempSelection = (store) => {
        setTempSelectedStoreIds((prev) => {
            const updated = prev.includes(store.id) ? prev : [...prev, store.id];
            if (updated.length === storesList.length) {
                setTempAllSelected(true);
            }
            return updated;
        });

        setTempSelectedStoreNames((prev) =>
            prev.some((item) => item.value === store.name)
                ? prev
                : [...prev, { label: store.name, value: store.name }]
        );
    };

    const removeStoreFromTempSelection = (store) => {
        setTempSelectedStoreIds((prev) => prev.filter((id) => id !== store.id));

        setTempSelectedStoreNames((prev) =>
            prev.filter((item) => item.value !== store.name)
        );

        setTempAllSelected(false);
    };

    const showOwnerColumn =
        typeof userDetailObj?.assigned_company?.level !== "undefined" &&
        ![3, 4].includes(parseInt(userDetailObj.assigned_company.level));

    const columns = React.useMemo(() => {
        const cols = [
            // Select checkbox column
            {
                id: "select",
                headerAlign: "center",
                align: "center",
                header: (
                    <ListingCellCenter>
                        <StyledWhiteCheckbox
                            size="small"
                            checked={tempAllSelected}
                            onChange={(e) => manageSelectAll(e.target.checked)}
                            indeterminate={tempSelectedStoreIds.length > 0 && tempSelectedStoreIds.length < storesList.length}
                        />
                    </ListingCellCenter>
                ),
                render: (store) => (
                    <ListingCellCenter>
                        <StyledCheckbox
                            size="small"
                            disabled={store.available === false}
                            checked={tempSelectedStoreIds.includes(store.id)}
                            onChange={(e) => handleStoreCheckboxChange(store, e.target.checked)}
                        />
                    </ListingCellCenter>
                ),
            },

            // Store Name
            {
                id: "name",
                header: (
                    <ListingHeaderContent onClick={() => handleSortingParamChange("name", sortConfig.name.dir)} style={{ cursor: "pointer" }}>
                        Store Name
                        <SortIcon direction={sortConfig.name.dir} />
                    </ListingHeaderContent>
                ),
                cellClassName: "text-wrapper-store_list wrapLargeText",
                render: (store) => (
                    <ListingCellLeft>
                        {store.name}
                    </ListingCellLeft>
                ),
            },

            // Address
            {
                id: "address",
                header: (
                    <ListingHeaderContent onClick={() => handleSortingParamChange("address", sortConfig.address.dir)} style={{ cursor: "pointer" }}>
                        Street &amp; No.
                        <SortIcon direction={sortConfig.address.dir} />
                    </ListingHeaderContent>
                ),
                cellClassName: "text-wrapper-store_list",
                render: (store) => <ListingCellLeft>{store.address}</ListingCellLeft>,
            },

            // Zip Code
            {
                id: "zipCode",
                cellSx: { width: "120px" },
                headerSx: { width: "120px" },
                header: (
                    <ListingHeaderContent onClick={() => handleSortingParamChange("zipcode", sortConfig.zipcode.dir)} style={{ cursor: "pointer" }}>
                        ZIP Code
                        <SortIcon direction={sortConfig.zipcode.dir} />
                    </ListingHeaderContent>
                ),
                cellClassName: "text-wrapper-store_list",
                render: (store) => <ListingCellLeft>{store.zipCode}</ListingCellLeft>,
            },

            // City
            {
                id: "city",
                header: (
                    <ListingHeaderContent onClick={() => handleSortingParamChange("city", sortConfig.city.dir)} style={{ cursor: "pointer" }}>
                        City
                        <SortIcon direction={sortConfig.city.dir} />
                    </ListingHeaderContent>
                ),
                cellClassName: "text-wrapper-store_list",
                render: (store) => <ListingCellLeft>{store.city}</ListingCellLeft>,
            },

            //Region
            {
                id: "region",
                header: (
                    <ListingHeaderContent onClick={() => handleSortingParamChange("region", sortConfig.region.dir)} style={{ cursor: "pointer" }}>
                        Region
                        <SortIcon direction={sortConfig.region.dir} />
                    </ListingHeaderContent>
                ),
                cellClassName: "text-wrapper-store_list wrapLargeText",
                render: (store) => <ListingCellLeft>{store.region?.name}</ListingCellLeft>,
            },
        ];

        //Conditional owner column
        if (showOwnerColumn) {
            cols.push({
                id: "company",
                header: (
                    <ListingHeaderContent onClick={() => handleSortingParamChange("owner", sortConfig.owner.dir)} style={{ cursor: "pointer" }}>
                        Owner
                        <SortIcon direction={sortConfig.owner.dir} />
                    </ListingHeaderContent>
                ),
                cellClassName: "text-wrapper-store_list wrapLargeText",
                render: (store) => <ListingCellLeft>{store.company?.name}</ListingCellLeft>,
            });
        }

        return cols;
    }, [
        sortConfig,
        storeDetails?.stores_list,
        tempSelectedStoreIds,
    ]);

    React.useEffect(() => {
        const reduxSelection = storeDetails[storeSelectionReduxKey] || {};
        const initialIds = reduxSelection.store_ids || [];
        const initialNames = reduxSelection.selected_store_names || [];
        const validateAllSelectedValue = initialIds.length > 0 && storesList.every(store => initialIds.includes(store.id));

        setTempSelectedStoreIds(initialIds);
        setTempSelectedStoreNames(initialNames);
        setTempAllSelected(validateAllSelectedValue);
    }, []);

    return (
        <Dialog open={open} onClose={onClose} maxWidth={"xl"} fullWidth>
            <DialogTitle sx={{ pr: 6 }}>
                <Typography variant="h6">
                    Detailed Store Selection
                </Typography>
            </DialogTitle>

            <DialogContent>
                {/* ---------- filters ---------- */}
                <Stack direction="row" spacing={2} flexWrap="wrap">
                    {/* MultiSelect for Store Name */}
                    <MultiSelect
                        label="Store Name"
                        options={storeOptions}
                        usedFor="filter"
                        selectionCheck={(o, v) => o.id === v.id}
                        defaultVal={storeNameFilter}
                        onChangeCall={setStoreNameFilter}
                    />

                    {/* MultiSelect for Address */}
                    <MultiSelect
                        label="Address"
                        options={addressOptions}
                        usedFor="filter"
                        selectionCheck={(o, v) => o.id === v.id}
                        defaultVal={addressFilter}
                        onChangeCall={setAddressFilter}
                    />

                    {/* MultiSelect for Owner; hidden if only one unique owner */}
                    {showOwnerColumn && ownerOptions.length > 1 && (
                        <MultiSelect
                            label="Owner"
                            options={ownerOptions}
                            usedFor="filter"
                            selectionCheck={(o, v) => o.id === v.id}
                            defaultVal={ownerFilter}
                            onChangeCall={setOwnerFilter}
                        />
                    )}

                    {/* Filter action buttons */}
                    <Stack direction="row" spacing={2} alignItems="center">
                        {hasFilters ? (
                            <ActionButton onClick={applyFilters} label="Apply" icon={<FilterAlt />} />
                        ) : (
                            <DisabledButton label="Apply" icon={<FilterAlt />} />
                        )}

                        {storeDetails.store_list_filter_applied ? (
                            <LightGreyButton onClick={clearFilters} label="Clear" icon={<Clear />} />
                        ) : (
                            <DisabledButton label="Clear" icon={<Clear />} />
                        )}
                    </Stack>
                </Stack>
                <Box height={25} />
                <ListingDataTableForModal
                    columns={columns}
                    rows={storeListitems}
                    rowKey={(store, idx) => `${store.number_store_cyreen ?? idx}_store_capacity`}
                    emptyText="No results"
                    // Don’t use the normal footer in a wizard modal:
                    pagination={null}
                    onPageChange={null}
                    onPageSizeChange={null}
                />

                {/* ----------- selected stores count ------------- */}
                <Box height={15} />
                <StyledDarkRedInformationTypography variant="body2">
                    Selected {tempSelectedStoreIds.length} of {storesList.length}
                </StyledDarkRedInformationTypography>

                <SpaceBetweenStack
                    direction={"row"}
                    spacing={2}
                    width={"100%"}
                    justifyContent={"space-between"}
                    display={"flex"}
                >
                    <Pagination
                        totalCount={listItems.length}
                        reduxPaginationKeyword="store_selection_pagination"
                        reduxStartKeyword="store_list_start"
                        reduxEndKeyword="store_list_end"
                    />
                </SpaceBetweenStack>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Stack direction="row" spacing={2}>
                    <CloseButton
                        onClick={handleCancel}
                        label="Cancel"
                        icon={<Close />}
                    />

                    <ActionButton
                        onClick={handleApply}
                        label="Apply"
                        icon={<Save />}
                    />
                </Stack>
            </DialogActions>
        </Dialog>
    );
};

export default StoreDetailTable;
